import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface KeydateItem {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  date: string | null; // ISO date string (YYYY-MM-DD)
  keydate_prompt: string | null;
  created_at: string;
  updated_at: string;
}

export function useKeydates(brandId: string | null) {
  const [items, setItems] = useState<KeydateItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const fetchItems = async () => {
    if (!brandId) {
      setItems([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cm_keydates')
        .select('*')
        .eq('brand_id', brandId)
        .order('date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setItems(data || []);
    } catch (e) {
      console.error('Error fetching keydates:', e);
      toast({ title: 'Error', description: 'Failed to fetch keydates', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = async (data: { name: string; description?: string; date?: string; enabled?: boolean; keydate_prompt?: string }) => {
    if (!brandId) throw new Error('No brand selected');
    setIsSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData.user?.id as string;
      const insert = {
        brand_id: brandId,
        user_id,
        name: data.name,
        description: data.description || null,
        enabled: data.enabled ?? true,
        date: data.date || null,
        keydate_prompt: data.keydate_prompt || null,
      };
      const { data: inserted, error } = await supabase.from('cm_keydates').insert(insert).select().single();
      if (error) throw error;
      setItems((prev) => [inserted as KeydateItem, ...prev]);
      return inserted as KeydateItem;
    } catch (e) {
      console.error('Error adding keydate:', e);
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  const updateItem = async (id: string, patch: Partial<Pick<KeydateItem, 'name' | 'description' | 'enabled' | 'date' | 'keydate_prompt'>>) => {
    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('cm_keydates')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      setItems((prev) => prev.map((i) => (i.id === id ? (data as KeydateItem) : i)));
      return data as KeydateItem;
    } catch (e) {
      console.error('Error updating keydate:', e);
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEnabled = async (id: string, enabled: boolean) => updateItem(id, { enabled });

  const removeItem = async (id: string) => {
    try {
      const { error } = await supabase.from('cm_keydates').delete().eq('id', id);
      if (error) throw error;
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      console.error('Error deleting keydate:', e);
      throw e;
    }
  };

  useEffect(() => {
    fetchItems();
  }, [brandId]);

  return { items, isLoading, isSaving, addItem, updateItem, toggleEnabled, removeItem, refetch: fetchItems };
}
